import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../../i18n/LanguageContext';

/* ── Reusable animated counter ── */
function Counter({ target, suffix = '', duration = 1800 }) {
  const [val, setVal] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStarted(true); }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const prog = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(prog * target));
      if (prog < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);

  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

/* ── Alex chat mockup (stays dark — product UI) ── */
function AIChatMockup() {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const id = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ background: 'rgba(13,11,30,0.97)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 48px rgba(34,28,81,0.18), 0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
        <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Alex — AI Media Buyer</span>
        <span style={{ marginLeft: 'auto', fontSize: 12 }} title="Chat history">🕘</span>
      </div>
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ background: 'rgba(34,28,81,0.6)', border: '1px solid rgba(124,58,237,0.4)', borderRadius: '12px 12px 2px 12px', padding: '8px 12px', maxWidth: '75%', fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>
            Audit my ad accounts and build a launch plan for my best product
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: 'linear-gradient(120deg,#a78bfa,#818cf8,#38bdf8)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12 }}>✦</div>
          <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px 12px 12px 12px', padding: '8px 12px', maxWidth: '82%' }}>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', margin: '0 0 8px', lineHeight: 1.5 }}>Audit done across 4 platforms — CTR 3.4%, best angle: video ads. Launch plan ready:</p>
            <div style={{ background: 'rgba(34,28,81,0.4)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 11, color: '#a78bfa', marginBottom: 4, fontWeight: 700 }}>MEDIA PLAN · 7 DAYS</div>
              <div style={{ fontSize: 12, color: '#fff' }}>D1-3 Engagement → D4-7 Conversion · 3 split tests</div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {['🎬 Generate video + image', '🎯 Improve targeting', '📅 Show schedule'].map((b) => (
                <span key={b} style={{ fontSize: 10, fontWeight: 700, color: '#c4b5fd', background: 'rgba(124,58,237,0.14)', border: '1px solid rgba(124,58,237,0.4)', borderRadius: 999, padding: '4px 10px', whiteSpace: 'nowrap' }}>{b}</span>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: 'linear-gradient(120deg,#a78bfa,#818cf8,#38bdf8)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12 }}>✦</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Applying media buying skill: creative-fatigue-analyzer{dots}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Lazy video: only sets src once scrolled into view (large mp4s) ── */
function LazyVideo({ src, label }) {
  const ref = useRef(null);
  const [load, setLoad] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setLoad(true); }, { rootMargin: '200px' });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(124,58,237,0.3)', boxShadow: '0 16px 48px rgba(34,28,81,0.22), 0 4px 12px rgba(0,0,0,0.12)', background: '#0A0821', aspectRatio: '16/9' }}>
      {load && (
        <video autoPlay muted loop playsInline preload="metadata" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}>
          <source src={src} type="video/mp4" />
        </video>
      )}
      <div style={{ position: 'absolute', left: 12, bottom: 12, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999, background: 'rgba(10,8,33,0.75)', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', fontSize: 11, fontWeight: 700, color: '#fff' }}>
        🎬 {label}
      </div>
    </div>
  );
}

/* ── Shopify mockup (stays dark) ── */
function ShopifyMockup() {
  const products = [
    { name: 'Signature Hoodie', price: '€49', score: 92, angle: 'UGC video' },
    { name: 'Travel Backpack', price: '€79', score: 88, angle: 'Problem/solution' },
    { name: 'Eco Water Bottle', price: '€19', score: 81, angle: 'Lifestyle demo' },
  ];
  return (
    <div style={{ background: 'rgba(13,11,30,0.97)', border: '1px solid rgba(149,191,71,0.3)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 48px rgba(34,28,81,0.18), 0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
        <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Shopify Catalog — AI Analysis</span>
      </div>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '9px 12px', background: 'rgba(149,191,71,0.08)', border: '1px solid rgba(149,191,71,0.25)', borderRadius: 10 }}>
          <span style={{ fontSize: 13 }}>🛍️</span>
          <span style={{ fontSize: 11, color: '#b9d98a' }}>24 products synced · analyzed by AI media buying skills</span>
        </div>
        {products.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', marginBottom: 7 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,#1f2937,#374151)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>📦</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>{p.name} <span style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 400 }}>· {p.price}</span></div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>Best angle: {p.angle}</div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: p.score > 90 ? '#34d399' : '#a78bfa' }}>{p.score}</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>ad score</div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#c4b5fd', background: 'rgba(124,58,237,0.14)', border: '1px solid rgba(124,58,237,0.4)', borderRadius: 999, padding: '4px 10px', flexShrink: 0 }}>Launch ad →</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Tracking mockup (stays dark) ── */
function TrackingMockup() {
  const events = [
    { ev: 'PageView', n: '12,480', color: '#60a5fa' },
    { ev: 'AddToCart', n: '1,932', color: '#a78bfa' },
    { ev: 'Purchase', n: '486', color: '#34d399' },
  ];
  return (
    <div style={{ background: 'rgba(13,11,30,0.97)', border: '1px solid rgba(96,165,250,0.25)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 48px rgba(34,28,81,0.18), 0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
        <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Tracking & Attribution</span>
      </div>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '10px 12px', background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 10 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', boxShadow: '0 0 6px #34d399' }} />
          <span style={{ fontSize: 11, color: '#6ee7b7' }}>Pixel active on your store · events flowing to all platforms</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {events.map((e, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: e.color, width: 80, flexShrink: 0 }}>{e.ev}</span>
              <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                <div style={{ width: `${100 - i * 34}%`, height: '100%', borderRadius: 4, background: `linear-gradient(90deg, ${e.color}66, ${e.color})` }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 800, color: '#fff', width: 52, textAlign: 'right', flexShrink: 0 }}>{e.n}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 14 }}>
          {[{ l: 'Attributed revenue', v: '€23,180', c: '#34d399' }, { l: 'Blended ROAS', v: '3.4x', c: '#a78bfa' }].map((s, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>{s.l}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: s.c }}>{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Multi-platform dashboard mockup (stays dark) ── */
function PlatformMockup() {
  const platforms = [
    { name: 'Meta', color: '#1877f2', spend: '€1,240', roas: '3.2x', status: 'live' },
    { name: 'TikTok', color: '#f0264e', spend: '€890', roas: '2.8x', status: 'live' },
    { name: 'LinkedIn', color: '#0a66c2', spend: '€560', roas: '4.1x', status: 'paused' },
    { name: 'Snapchat', color: '#fffc00', spend: '€340', roas: '2.1x', status: 'live' },
  ];
  return (
    <div style={{ background: 'rgba(13,11,30,0.97)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 48px rgba(34,28,81,0.18), 0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
        <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Campaign Manager — All Platforms</span>
      </div>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
          {[{ label: 'Total Spend', val: '€3,030', color: '#a78bfa' }, { label: 'Avg. ROAS', val: '3.05x', color: '#34d399' }, { label: 'Impressions', val: '128K', color: '#60a5fa' }, { label: 'Active Ads', val: '14', color: '#fbbf24' }].map((s, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.val}</div>
            </div>
          ))}
        </div>
        {platforms.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', marginBottom: 6, border: '1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', flex: 1, fontWeight: 600 }}>{p.name}</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{p.spend}</span>
            <span style={{ fontSize: 11, color: '#34d399', fontWeight: 700 }}>{p.roas}</span>
            <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: p.status === 'live' ? 'rgba(52,211,153,0.15)' : 'rgba(251,191,36,0.15)', color: p.status === 'live' ? '#34d399' : '#fbbf24', fontWeight: 700 }}>{p.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Analytics chart mockup (stays dark) ── */
function AnalyticsMockup() {
  const bars = [40, 65, 50, 80, 72, 95, 88, 100, 92, 78, 85, 98];
  return (
    <div style={{ background: 'rgba(13,11,30,0.97)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 48px rgba(34,28,81,0.18), 0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
        <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Live Analytics — Last 30 days</span>
      </div>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          {[{ label: 'CTR', val: '4.8%', delta: '+0.6%', up: true, color: '#34d399' }, { label: 'CPA', val: '€4.20', delta: '-12%', up: false, color: '#a78bfa' }, { label: 'ROAS', val: '3.4x', delta: '+0.8x', up: true, color: '#60a5fa' }].map((m, i) => (
            <div key={i} style={{ flex: 1, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 3 }}>{m.label}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: m.color }}>{m.val}</div>
              <div style={{ fontSize: 10, color: m.up ? '#34d399' : '#f87171', marginTop: 2 }}>{m.delta}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
          {bars.map((h, i) => (
            <div key={i} style={{ flex: 1, borderRadius: '3px 3px 0 0', background: i === bars.length - 1 ? 'linear-gradient(180deg,#221C51,#4F46E5)' : 'rgba(34,28,81,0.5)', height: `${h}%`, transition: `height 0.6s ${i * 50}ms ease` }} />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
          {['Apr 1', '', '', 'Apr 8', '', '', 'Apr 15', '', '', 'Apr 22', '', 'Apr 30'].map((l, i) => (
            <span key={i} style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)' }}>{l}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Automation rules mockup (stays dark) ── */
function AutomationMockup() {
  const rules = [
    { if: 'CPA > €8', then: 'Pause ad set', triggered: 2 },
    { if: 'ROAS > 4x', then: 'Increase budget +20%', triggered: 5 },
    { if: 'CTR < 1%', then: 'Alert me via email', triggered: 1 },
  ];
  return (
    <div style={{ background: 'rgba(13,11,30,0.97)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 48px rgba(34,28,81,0.18), 0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
        <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Automation Rules</span>
      </div>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '10px 12px', background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 10 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399' }} />
          <span style={{ fontSize: 11, color: '#6ee7b7' }}>3 rules active · 8 actions triggered this week</span>
        </div>
        {rules.map((r, i) => (
          <div key={i} style={{ padding: '12px', borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#fbbf24', background: 'rgba(251,191,36,0.1)', padding: '2px 7px', borderRadius: 4 }}>IF</span>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>{r.if}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#a78bfa', background: 'rgba(124,58,237,0.1)', padding: '2px 7px', borderRadius: 4 }}>THEN</span>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>{r.then}</span>
              <span style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>×{r.triggered}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Feature row ── */
function FeatureRow({ tag, tagColor, tagBg, tagBorder, headline, headlineAccent, sub, bullets, bulletColor, stats, visual, reverse, visible }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))',
      gap: 56,
      alignItems: 'center',
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : 'translateY(32px)',
      transition: 'all 0.7s ease',
    }}>
      <div style={{ order: reverse ? 2 : 1 }}>
        <span style={{ display: 'inline-block', padding: '4px 14px', borderRadius: 999, background: tagBg, border: `1px solid ${tagBorder}`, color: tagColor, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 20 }}>
          {tag}
        </span>
        <h3 style={{ fontSize: 'clamp(22px,3vw,32px)', fontWeight: 800, color: '#ffffff', letterSpacing: '-0.02em', marginBottom: 14, lineHeight: 1.25 }}>
          {headline}
          {headlineAccent && (
            <span style={{ background: `linear-gradient(135deg,${tagColor},#4F46E5)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              {' '}{headlineAccent}
            </span>
          )}
        </h3>
        <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.62)', lineHeight: 1.7, marginBottom: 24 }}>{sub}</p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {bullets.map((b, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'rgba(255,255,255,0.78)' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: bulletColor, flexShrink: 0 }} />
              {b}
            </li>
          ))}
        </ul>
        {stats && (
          <div style={{ display: 'flex', gap: 28, marginTop: 32, flexWrap: 'wrap' }}>
            {stats.map((s, i) => (
              <div key={i} style={{ paddingRight: i < stats.length - 1 ? 28 : 0, borderRight: i < stats.length - 1 ? '1px solid rgba(255,255,255,0.12)' : 'none' }}>
                <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: '-0.02em', background: 'linear-gradient(135deg,#fff 20%,#b9b1ff)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  <Counter target={s.num} suffix={s.suffix} />
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.42)', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ order: reverse ? 1 : 2 }}>
        {visual}
      </div>
    </div>
  );
}

const Features = () => {
  const { t } = useLang();
  const [v1, setV1] = useState(false);
  const [v2, setV2] = useState(false);
  const [v3, setV3] = useState(false);
  const [v4, setV4] = useState(false);
  const [v5, setV5] = useState(false);
  const [v6, setV6] = useState(false);
  const [v7, setV7] = useState(false);
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];
  const setters = [setV1, setV2, setV3, setV4, setV5, setV6, setV7];

  useEffect(() => {
    refs.forEach((r, i) => {
      const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setters[i](true); }, { threshold: 0.15 });
      if (r.current) obs.observe(r.current);
    });
  }, []);

  return (
    <section id="features" style={{ background: '#0A0722', padding: '100px 24px', position: 'relative', overflow: 'hidden' }}>
      <div className="oba-aurora" style={{ top: '6%', left: '-12%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(109,93,251,.2) 0%, transparent 68%)' }} />
      <div className="oba-aurora" style={{ bottom: '4%', right: '-12%', width: 560, height: 560, background: 'radial-gradient(circle, rgba(56,189,248,.16) 0%, transparent 68%)' }} />
      <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>

        {/* Section header */}
        <div style={{ textAlign: 'center', marginBottom: 80 }}>
          <span style={{ display: 'inline-block', padding: '5px 16px', borderRadius: 999, background: 'rgba(109,93,251,0.14)', border: '1px solid rgba(109,93,251,0.38)', color: '#c7bfff', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 18 }}>
            {t('landing.features.badge')}
          </span>
          <h2 style={{ fontSize: 'clamp(28px,4vw,46px)', fontWeight: 900, color: '#ffffff', letterSpacing: '-0.03em', marginBottom: 14, lineHeight: 1.15 }}>
            {t('landing.features.title1')}{' '}
            <span style={{ background: 'linear-gradient(120deg,#a78bfa,#818cf8,#38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('landing.features.title2')}</span>
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.62)', maxWidth: 520, margin: '0 auto', lineHeight: 1.65 }}>
            {t('landing.features.sub')}
          </p>
        </div>

        {/* Feature row 1 — Alex AI media buyer */}
        <div ref={refs[0]} style={{ marginBottom: 100 }}>
          <FeatureRow
            visible={v1}
            tag={t('landing.features.alexTag')}
            tagColor="#221C51"
            tagBg="rgba(34,28,81,0.07)"
            tagBorder="rgba(34,28,81,0.18)"
            headline={t('landing.features.alexHeadline')}
            headlineAccent={t('landing.features.alexAccent')}
            sub={t('landing.features.alexSub')}
            bullets={[
              t('landing.features.alexB1'),
              t('landing.features.alexB2'),
              t('landing.features.alexB3'),
              t('landing.features.alexB4'),
            ]}
            bulletColor="#221C51"
            stats={[
              { num: 60, suffix: '+', label: t('landing.features.alexS1') },
              { num: 5, suffix: '', label: t('landing.features.alexS2') },
            ]}
            visual={<AIChatMockup />}
          />
        </div>

        {/* Feature row 1b — Cinema Studio (real AI-generated video) */}
        <div ref={refs[4]} style={{ marginBottom: 100 }}>
          <FeatureRow
            visible={v5}
            reverse
            tag={t('landing.features.studioTag')}
            tagColor="#7c3aed"
            tagBg="rgba(124,58,237,0.07)"
            tagBorder="rgba(124,58,237,0.25)"
            headline={t('landing.features.studioHeadline')}
            headlineAccent={t('landing.features.studioAccent')}
            sub={t('landing.features.studioSub')}
            bullets={[
              t('landing.features.studioB1'),
              t('landing.features.studioB2'),
              t('landing.features.studioB3'),
              t('landing.features.studioB4'),
            ]}
            bulletColor="#7c3aed"
            stats={[
              { num: 3, suffix: '', label: t('landing.features.studioS1') },
              { num: 60, suffix: 's', label: t('landing.features.studioS2') },
            ]}
            visual={<LazyVideo src="/videos/showcase-1.mp4" label="Generated in Cinema Studio" />}
          />
        </div>

        {/* Feature row 1c — Shopify */}
        <div ref={refs[5]} style={{ marginBottom: 100 }}>
          <FeatureRow
            visible={v6}
            tag={t('landing.features.shopifyTag')}
            tagColor="#5E8E3E"
            tagBg="rgba(149,191,71,0.1)"
            tagBorder="rgba(94,142,62,0.3)"
            headline={t('landing.features.shopifyHeadline')}
            headlineAccent={t('landing.features.shopifyAccent')}
            sub={t('landing.features.shopifySub')}
            bullets={[
              t('landing.features.shopifyB1'),
              t('landing.features.shopifyB2'),
              t('landing.features.shopifyB3'),
              t('landing.features.shopifyB4'),
            ]}
            bulletColor="#5E8E3E"
            stats={[
              { num: 1, suffix: '-click', label: t('landing.features.shopifyS1') },
              { num: 100, suffix: '%', label: t('landing.features.shopifyS2') },
            ]}
            visual={<ShopifyMockup />}
          />
        </div>

        {/* Feature row 1d — Tracking */}
        <div ref={refs[6]} style={{ marginBottom: 100 }}>
          <FeatureRow
            visible={v7}
            reverse
            tag={t('landing.features.trackingTag')}
            tagColor="#2563EB"
            tagBg="rgba(37,99,235,0.07)"
            tagBorder="rgba(37,99,235,0.22)"
            headline={t('landing.features.trackingHeadline')}
            headlineAccent={t('landing.features.trackingAccent')}
            sub={t('landing.features.trackingSub')}
            bullets={[
              t('landing.features.trackingB1'),
              t('landing.features.trackingB2'),
              t('landing.features.trackingB3'),
              t('landing.features.trackingB4'),
            ]}
            bulletColor="#2563EB"
            stats={[
              { num: 100, suffix: '%', label: t('landing.features.trackingS1') },
              { num: 24, suffix: '/7', label: t('landing.features.trackingS2') },
            ]}
            visual={<TrackingMockup />}
          />
        </div>

        {/* Feature row 2 — Multi-platform */}
        <div ref={refs[1]} style={{ marginBottom: 100 }}>
          <FeatureRow
            visible={v2}
            reverse
            tag={t('landing.features.multiTag')}
            tagColor="#1877f2"
            tagBg="rgba(24,119,242,0.07)"
            tagBorder="rgba(24,119,242,0.2)"
            headline={t('landing.features.multiHeadline')}
            headlineAccent={t('landing.features.multiAccent')}
            sub={t('landing.features.multiSub')}
            bullets={[
              t('landing.features.multiB1'),
              t('landing.features.multiB2'),
              t('landing.features.multiB3'),
              t('landing.features.multiB4'),
            ]}
            bulletColor="#1877f2"
            stats={[
              { num: 5, suffix: '+', label: t('landing.features.multiS1') },
              { num: 70, suffix: '%', label: t('landing.features.multiS2') },
            ]}
            visual={<PlatformMockup />}
          />
        </div>

        {/* Feature row 3 — Analytics */}
        <div ref={refs[2]} style={{ marginBottom: 100 }}>
          <FeatureRow
            visible={v3}
            tag={t('landing.features.analyticsTag')}
            tagColor="#059669"
            tagBg="rgba(5,150,105,0.07)"
            tagBorder="rgba(5,150,105,0.2)"
            headline={t('landing.features.analyticsHeadline')}
            headlineAccent={t('landing.features.analyticsAccent')}
            sub={t('landing.features.analyticsSub')}
            bullets={[
              t('landing.features.analyticsB1'),
              t('landing.features.analyticsB2'),
              t('landing.features.analyticsB3'),
              t('landing.features.analyticsB4'),
            ]}
            bulletColor="#059669"
            stats={[
              { num: 128, suffix: 'K', label: t('landing.features.analyticsS1') },
              { num: 98, suffix: '%', label: t('landing.features.analyticsS2') },
            ]}
            visual={<AnalyticsMockup />}
          />
        </div>

        {/* Feature row 4 — Automation */}
        <div ref={refs[3]}>
          <FeatureRow
            visible={v4}
            reverse
            tag={t('landing.features.autoTag')}
            tagColor="#D97706"
            tagBg="rgba(217,119,6,0.07)"
            tagBorder="rgba(217,119,6,0.2)"
            headline={t('landing.features.autoHeadline')}
            headlineAccent={t('landing.features.autoAccent')}
            sub={t('landing.features.autoSub')}
            bullets={[
              t('landing.features.autoB1'),
              t('landing.features.autoB2'),
              t('landing.features.autoB3'),
              t('landing.features.autoB4'),
            ]}
            bulletColor="#D97706"
            stats={[
              { num: 24, suffix: '/7', label: t('landing.features.autoS1') },
              { num: 40, suffix: '%', label: t('landing.features.autoS2') },
            ]}
            visual={<AutomationMockup />}
          />
        </div>

      </div>
    </section>
  );
};

export default Features;
